import { ColDef, ValueFormatterParams } from 'ag-grid-community';
import {
    ECHELON_DURATION,
    ECHELON_FIELD,
    ECHELON_FILIERE,
    ECHELON_GRADE,
    POSITION_NUMBER,
} from 'routes/HumanResourcesModule/business/definitions';
import { TEXT_FILTER } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/defaultColDefs';
import { getAgentColumn } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/agentColumnHelpers';
import { Echelon } from 'routes/HumanResourcesModule/business/types';
import { t } from 'i18next';
import { getGroupedOrgChartHrColumn } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/orgChartHrColumnHelpers';
import { getMantyStatusColumn } from 'routes/HumanResourcesModule/PayState/helpers/colDefinitions';

/**
 * Converts a floating-point number representing years into an object containing whole years and remaining months.
 *
 * @param yearsDuration - The duration in years as a floating-point number. For example, 2.5 represents two years and six months.
 * @returns An object with two properties: `years` representing the whole number of years, and `months` representing the remaining months.
 *          If `yearsDuration` is undefined or less than zero, returns `{ years: 0, months: 0 }`.
 *
 * @example
 * floatYearsToYearsAndMonth(2.5); // returns: { years: 2, months: 6 }
 */
const floatYearsToYearsAndMonth = (
    yearsDuration?: number | null,
): { years: number; months: number } => {
    if (!yearsDuration || yearsDuration < 0) {
        return { years: 0, months: 0 };
    }

    const whole_year = Math.floor(yearsDuration);
    const months = Math.floor((yearsDuration - whole_year) * 12);
    return { years: whole_year, months };
};

const durationValueFormatter = (
    params: ValueFormatterParams<Echelon, number | undefined>,
) => {
    const { years, months } = floatYearsToYearsAndMonth(params.value);
    const years_msg = `${years} ${t('common.years', { count: years })}`;
    if (months === 0) {
        return years_msg;
    }
    return `${years_msg} ${months} ${t('common.month')}`;
};

export const getColDefs = (): ColDef[] => [
    getGroupedOrgChartHrColumn(),
    {
        ...getAgentColumn(),
        width: 200,
    },
    getMantyStatusColumn(),
    ...[ECHELON_FIELD, ECHELON_GRADE, ECHELON_FILIERE, POSITION_NUMBER].map(
        (field) => ({
            ...TEXT_FILTER,
            headerName: t(`humanResources.payState.${field}`),
            field,
            enableRowGroup: true,
        }),
    ),
    {
        ...TEXT_FILTER,
        headerName: t('humanResources.payState.echelonDuration'),
        field: ECHELON_DURATION,
        valueFormatter: durationValueFormatter,
        enableRowGroup: true,
        aggFunc: 'sum',
    },
];
